import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { formatDateLocal } from "../../../../utils/hooks/dataHandler";
import {
  convertReferenceAmountToSgd,
  formatMainCompSavedAt,
  formatRefAmount,
} from "./formCSReferenceUtils";
import { buildFormCSFieldReferenceSections } from "./formCSFieldReferenceHints";

const COMPACT_REFERENCE_PANEL_ZOOM_THRESHOLD = 1.25;
const COMPACT_REFERENCE_PANEL_WIDTH_THRESHOLD = 1100;

const getReferencePanelZoomLevel = () => {
  if (typeof window === "undefined") return 1;

  if (window.visualViewport?.scale && window.visualViewport.scale !== 1) {
    return window.visualViewport.scale;
  }

  return window.devicePixelRatio || 1;
};

const shouldUseCompactReferencePanelHeader = () => {
  if (typeof window === "undefined") return false;

  return (
    getReferencePanelZoomLevel() >= COMPACT_REFERENCE_PANEL_ZOOM_THRESHOLD ||
    window.innerWidth <= COMPACT_REFERENCE_PANEL_WIDTH_THRESHOLD
  );
};

const formatHintValue = (currency, field) => {
  if (field.value == null || field.value === "") return "-";
  return String(field.value);
};

const ReferenceAmountDisplay = ({
  currency,
  value,
  sgdValue,
  fontWeight = 600,
  color = "text.primary",
}) => {
  if (value == null || Number.isNaN(value)) {
    return (
      <Typography variant='body2' fontWeight={fontWeight} color={color}>
        -
      </Typography>
    );
  }

  if (typeof value !== "number") {
    return (
      <Typography variant='body2' fontWeight={fontWeight} color={color}>
        {String(value)}
      </Typography>
    );
  }

  const isForeignCurrency = currency && currency !== "SGD";
  const convertedSgd = typeof sgdValue === "number" ? sgdValue : null;
  const displayCurrency =
    isForeignCurrency && convertedSgd != null ? "SGD" : currency;
  const displayValue =
    isForeignCurrency && convertedSgd != null ? convertedSgd : value;

  return (
    <Box sx={{ mt: 0.25 }}>
      <Typography variant='body2' fontWeight={fontWeight} color={color}>
        {formatRefAmount(displayCurrency, displayValue)}
      </Typography>
      {isForeignCurrency && convertedSgd != null ? (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: "block", mt: 0.1 }}
        >
          {formatRefAmount(currency, value)}
        </Typography>
      ) : null}
    </Box>
  );
};

const ReferenceFieldRow = ({ currency, field }) => (
  <Box sx={{ py: 0.75 }}>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ display: "block", lineHeight: 1.3 }}
    >
      {field.label}
      {process.env.REACT_APP_RENDER_ENV === "DEV" && (
        <Typography
          component='span'
          variant='caption'
          sx={{
            ml: 0.5,
            opacity: 0.65,
            fontFamily: "monospace",
            fontSize: "0.65rem",
          }}
        >
          {field.key}
        </Typography>
      )}
    </Typography>
    {typeof field.value === "number" ? (
      <ReferenceAmountDisplay
        currency={currency}
        value={field.value}
        sgdValue={field.sgdValue}
      />
    ) : (
      <Typography variant='body2' fontWeight={600} sx={{ mt: 0.25 }}>
        {formatHintValue(currency, field)}
      </Typography>
    )}
    {field.hint ? (
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: "block", mt: 0.25 }}
      >
        {field.hint}
      </Typography>
    ) : null}
  </Box>
);

const FormCSDataFormReferencePanel = ({
  selectedTc,
  referenceSummary,
  parsedMainComp,
  plSnapshot,
  mainCompSavedAt,
  assistantLoading,
  assistantError,
  onDownloadPdf,
  onFillReference,
  fillReferenceCount = 0,
  pdfGenerating,
  disabled,
}) => {
  const [expanded, setExpanded] = useState("module-d");
  const [useCompactHeader, setUseCompactHeader] = useState(() =>
    shouldUseCompactReferencePanelHeader()
  );
  const cur = referenceSummary?.currency || selectedTc?.currency || "SGD";
  const savedLabel = formatMainCompSavedAt(mainCompSavedAt);
  const getSummarySgdValue = (value) =>
    referenceSummary?.isForeignCurrency
      ? convertReferenceAmountToSgd(value, referenceSummary)
      : null;
  const taxSummaryCurrency = referenceSummary?.isForeignCurrency ? "SGD" : cur;

  const fieldSections = useMemo(
    () =>
      buildFormCSFieldReferenceSections(parsedMainComp, plSnapshot, selectedTc),
    [parsedMainComp, plSnapshot, selectedTc]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateHeaderDensity = () => {
      setUseCompactHeader(shouldUseCompactReferencePanelHeader());
    };

    updateHeaderDensity();
    window.addEventListener("resize", updateHeaderDensity);
    window.visualViewport?.addEventListener("resize", updateHeaderDensity);

    return () => {
      window.removeEventListener("resize", updateHeaderDensity);
      window.visualViewport?.removeEventListener("resize", updateHeaderDensity);
    };
  }, []);

  if (!selectedTc) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          height: "100%",
          borderRadius: 2,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Typography variant='subtitle2' fontWeight={700} gutterBottom>
          Reference
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Select a tax computation to view suggested values for Form C-S fields
          and fill available amounts and dates.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: useCompactHeader ? 1.25 : 2,
        height: "100%",
        maxHeight: { md: "calc(100vh - 220px)" },
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha("#603F97", 0.35),
        bgcolor: alpha("#603F97", 0.04),
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ gap: 1, mb: useCompactHeader ? 0.75 : 1, flexShrink: 0 }}
      >
        <Typography variant='subtitle2' fontWeight={700} sx={{ minWidth: 0 }}>
          Reference
        </Typography>
        <Stack direction='row' alignItems='center' spacing={0.75}>
          <Tooltip title='Automatically fill available fields based on the last saved tax computation.'>
            <span>
              <Button
                size='small'
                variant='contained'
                startIcon={<AutoFixHighIcon fontSize='small' />}
                onClick={onFillReference}
                disabled={
                  disabled ||
                  assistantLoading ||
                  !fillReferenceCount ||
                  !onFillReference
                }
                sx={{
                  borderRadius: 1,
                  minWidth: useCompactHeader ? 64 : undefined,
                  px: useCompactHeader ? 1 : undefined,
                  textTransform: "none",
                }}
              >
                Fill
              </Button>
            </span>
          </Tooltip>
          <Tooltip title='Download Main Tax Computation PDF'>
            <span>
              <IconButton
                size='small'
                color='primary'
                aria-label='Download Main Tax Computation PDF'
                onClick={onDownloadPdf}
                disabled={
                  disabled ||
                  assistantLoading ||
                  !referenceSummary ||
                  pdfGenerating
                }
              >
                {pdfGenerating ? (
                  <CircularProgress size={20} />
                ) : (
                  <PictureAsPdfIcon fontSize='small' />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography
        variant='caption'
        color='text.secondary'
        display='block'
        sx={{ mb: 1, flexShrink: 0 }}
      >
        Based on your last saved tax computation.
        <b> Please review and verify all information carefully before use.</b>
      </Typography>

      <Stack
        direction='row'
        gap={0.5}
        flexWrap={useCompactHeader ? "nowrap" : "wrap"}
        useFlexGap
        sx={{
          mb: useCompactHeader ? 1 : 1.5,
          flexShrink: 0,
          minHeight: useCompactHeader ? 24 : undefined,
          overflowX: useCompactHeader ? "auto" : "visible",
          overflowY: "hidden",
          pb: useCompactHeader ? 0.25 : 0,
          scrollbarWidth: "thin",
          "& .MuiChip-root": {
            flexShrink: 0,
            height: useCompactHeader ? 22 : 24,
          },
        }}
      >
        {selectedTc?.yearOfAssessment != null && (
          <Chip size='small' label={`YA ${selectedTc.yearOfAssessment}`} />
        )}
        {selectedTc?.financialYearEnd && (
          <Chip
            size='small'
            variant='outlined'
            label={`FYE ${formatDateLocal(new Date(selectedTc.financialYearEnd))}`}
          />
        )}
        <Chip size='small' variant='outlined' label={cur} />
        {referenceSummary?.isStartup ? (
          <Chip size='small' color='secondary' label='Startup' />
        ) : null}
      </Stack>

      {assistantLoading ? (
        <Stack direction='row' alignItems='center' gap={1} sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant='body2' color='text.secondary'>
            Loading…
          </Typography>
        </Stack>
      ) : assistantError ? (
        <Alert severity='warning' sx={{ py: 0.5 }}>
          {assistantError}
        </Alert>
      ) : !referenceSummary && !fieldSections?.length ? (
        <Typography variant='body2' color='text.secondary'>
          No saved Main Tax Computation snapshot for this year.
        </Typography>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            mr: -0.5,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {referenceSummary ? (
            <Stack spacing={useCompactHeader ? 0.75 : 1} sx={{ mb: 1.25 }}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {savedLabel ? (
                    <>
                      Saved {savedLabel}
                      <br />
                    </>
                  ) : null}
                  {cur && cur !== "SGD" ? (
                    <>
                      Exchange Rate: 1 {cur} ={" "}
                      {referenceSummary.currencyRate || "1.00"}{" "}
                      SGD
                      <br />
                    </>
                  ) : null}
                  Net Profit / (Loss) before Tax
                </Typography>
                <ReferenceAmountDisplay
                  currency={cur}
                  value={referenceSummary.npbt}
                  sgdValue={
                    referenceSummary.npbtSgd ??
                    getSummarySgdValue(referenceSummary.npbt)
                  }
                  fontWeight={700}
                />
              </Box>
              <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Chargeable income
                  </Typography>
                  <ReferenceAmountDisplay
                    currency={taxSummaryCurrency}
                    value={referenceSummary.chargebleIncomeAfterExempt}
                  />
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Net tax payable
                  </Typography>
                  <ReferenceAmountDisplay
                    currency={taxSummaryCurrency}
                    value={referenceSummary.netTaxPayable}
                    fontWeight={700}
                    color='primary.main'
                  />
                </Box>
              </Stack>
            </Stack>
          ) : null}

          {fieldSections?.length ? (
            <>
              {fieldSections.map((section) => (
                <Accordion
                  key={section.id}
                  disableGutters
                  elevation={0}
                  expanded={expanded === section.id}
                  onChange={(_, isOpen) =>
                    setExpanded(isOpen ? section.id : false)
                  }
                  sx={{
                    bgcolor: "transparent",
                    "&:before": { display: "none" },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "4px !important",
                    mb: 0.75,
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize='small' />}
                    sx={{
                      minHeight: 40,
                      px: 1.25,
                      bgcolor: "background.paper",
                      "& .MuiAccordionSummary-content": { my: 0.75 },
                    }}
                  >
                    <Typography variant='caption' fontWeight={700}>
                      {section.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1 }}>
                    <Stack divider={<Divider flexItem />}>
                      {section.fields.map((field) => (
                        <ReferenceFieldRow
                          key={field.key}
                          currency={cur}
                          field={field}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          ) : null}
        </Box>
      )}
    </Paper>
  );
};

export default FormCSDataFormReferencePanel;
